import { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

interface NavigationHistoryState {
  canGoBack: boolean;
  canGoForward: boolean;
  currentPath: string;
  goBack: () => void;
  goForward: () => void;
}

export const useNavigationHistory = (): NavigationHistoryState => {
  const history = useHistory();
  const location = useLocation();
  const [hasNavigatedInApp, setHasNavigatedInApp] = useState(false);
  const [hasForwardHistory, setHasForwardHistory] = useState(false);

  // Track when navigation happens within the app
  useEffect(
    () =>
      history.listen((_, action) => {
        if (action === "PUSH") {
          setHasNavigatedInApp(true);
          // When we push a new location, we lose any forward history
          setHasForwardHistory(false);
        } else if (action === "POP") {
          // POP happens on back/forward navigation
          // We can't easily tell if it was back or forward, so we'll be conservative
          setHasNavigatedInApp(true);
        }
      }),
    [history],
  );

  const goBack = useCallback(() => {
    // When we go back, we create forward history
    setHasForwardHistory(true);
    history.goBack();
  }, [history]);

  const goForward = useCallback(() => {
    // When we go forward, we might lose forward history (if we're at the end)
    // We'll let the next navigation event update the state
    history.goForward();
  }, [history]);

  return {
    canGoBack: hasNavigatedInApp,
    canGoForward: hasForwardHistory,
    currentPath: `${location.pathname}${location.search}${location.hash}`,
    goBack,
    goForward,
  };
};
